import { useState, useCallback, memo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "@/lib/toast-with-sound";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { 
  Calendar, Clock, Gauge, MapPin, Route, Users, Eye, Edit, 
  Image as ImageIcon, Globe, Lock, FileEdit,
  Navigation, Mountain, ThumbsUp, ThumbsDown, Pin, Check, AlertCircle, Star, PlayCircle
} from "lucide-react";
import { getTranslatedValue } from "./TranslatableField";
import { Badge } from "./ui/badge";
import { PlanBadgeIcon } from "./PlanBadgeAvatar";
import api from "@/lib/api";
import { formStyles } from "@/lib/form-styles";
import { handleUnauthorizedAccess } from "@/lib/access-control";
import { getUploadUrl, getCloudinaryThumbnail, quoteNickname } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useLimits } from "@/contexts/LimitsContext";

const MOTRI_AVATAR = "/images/motri-head-sm.png";

// V-Hand icon (motorcyclist greeting / peace sign) - custom SVG component
const VHandIcon = ({ className, filled = false }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none"
    className={className}
  >
    {/* Hand palm/base */}
    <path 
      d="M8 14V8.5C8 7.67 8.67 7 9.5 7S11 7.67 11 8.5V14M11 8.5V6.5C11 5.67 11.67 5 12.5 5S14 5.67 14 6.5V14M14 8.5C14 7.67 14.67 7 15.5 7S17 7.67 17 8.5V14M17 11.5C17 10.67 17.67 10 18.5 10S20 10.67 20 11.5V16C20 19.31 17.31 22 14 22H12C8.69 22 6 19.31 6 16V14.5C6 13.67 6.67 13 7.5 13S9 13.67 9 14.5" 
      stroke="currentColor" 
      strokeWidth="1.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      fill={filled ? "currentColor" : "none"}
    />
    {/* Victory fingers extended */}
    <path d="M11 8.5V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M14 6.5V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    {/* Sparkles */}
    <circle cx="9" cy="4" r="1" fill="#FFD700" />
    <circle cx="16" cy="2" r="1" fill="#FFD700" />
  </svg>
);

// Trip type definitions with icons and colors (labels come from translations)
const TRIP_TYPE_CONFIG = {
  nacionais: { icon: Navigation, color: "text-blue-500" },
  touring: { icon: Globe, color: "text-orange-500" },
  offroad: { icon: Mountain, color: "text-red-500" },
  // Legacy types for backwards compatibility
  estrada: { icon: Navigation, color: "text-blue-500" },
  aventura: { icon: Mountain, color: "text-red-500" },
};

// Difficulty definitions with colors (labels come from translations)
const DIFFICULTY_CONFIG = {
  green: { color: "bg-green-500" },
  blue: { color: "bg-blue-500" },
  red: { color: "bg-yellow-500" },
  black: { color: "bg-red-500" },
};

// Helper functions
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" });
};

const formatDuration = (hours) => {
  if (!hours) return "";
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

// Publication status config (replaces visibility for trips)
const PUBLICATION_STATUS_CONFIG = {
  draft: { icon: Globe, color: "text-zinc-400" },
  active: { icon: Globe, color: "text-green-500" },
  closed: { icon: Lock, color: "text-amber-500" },
  archived: { icon: Lock, color: "text-slate-400" },
};

// Check if trip has photos (main_photo, in segments or legacy field)
const tripHasPhotos = (trip) => {
  if (trip.main_photo) return true;
  if (trip.photos && trip.photos.length > 0) return true;
  if (trip.segments) {
    for (const seg of trip.segments) {
      if (seg.photos && seg.photos.length > 0) return true;
    }
  }
  return false;
};

// Get main photo URL
const getMainPhotoUrl = (trip) => {
  if (trip.main_photo) {
    return getUploadUrl(trip.main_photo);
  }
  if (trip.segments) {
    for (const seg of trip.segments) {
      if (seg.photos && seg.photos.length > 0) {
        return getUploadUrl(seg.photos[0].filename);
      }
    }
  }
  if (trip.photos && trip.photos.length > 0) {
    const mainPhoto = trip.photos.find(p => typeof p === 'object' && p.is_main);
    if (mainPhoto) return getUploadUrl(mainPhoto.filename);
    const photo = trip.photos[0];
    const filename = typeof photo === 'string' ? photo : photo?.filename;
    return filename ? getUploadUrl(filename) : null;
  }
  return null;
};

// Get thumbnail URL for card display (optimized size)
const getMainPhotoThumbnail = (trip, width = 400, height = 250) => {
  const url = getMainPhotoUrl(trip);
  return getCloudinaryThumbnail(url, width, height);
};

// Get avatar thumbnail (small size for participant lists)
const getAvatarThumbnail = (url, size = 96) => {
  if (!url) return null;
  const fullUrl = url.startsWith('http') ? url : getUploadUrl(url);
  return getCloudinaryThumbnail(fullUrl, size, size);
};

// Count total photos
const getTotalPhotosCount = (trip) => {
  // [PERF] Prefer the server-computed aggregate; fall back to iterating
  // segments for older payloads that don't carry it yet.
  if (typeof trip.total_photo_count === "number") return trip.total_photo_count;
  let count = 0;
  if (trip.segments) {
    for (const seg of trip.segments) {
      count += seg.photo_count ?? seg.photos?.length ?? 0;
    }
  }
  count += trip.photos?.length || 0;
  return count;
};

// Get unique trip types from segments
const getTripTypes = (trip) => {
  if (Array.isArray(trip.segment_types)) return trip.segment_types;
  const types = new Set();
  if (trip.segments) {
    for (const seg of trip.segments) {
      if (seg.segment_type) types.add(seg.segment_type);
    }
  }
  return Array.from(types);
};

// Get unique difficulties from segments
const getTripDifficulties = (trip) => {
  if (Array.isArray(trip.difficulties)) return trip.difficulties;
  const diffs = new Set();
  if (trip.segments) {
    for (const seg of trip.segments) {
      if (seg.difficulty) diffs.add(seg.difficulty);
    }
  }
  return Array.from(diffs);
};

// Export helper functions for use in other components
export { tripHasPhotos, getMainPhotoUrl, getTotalPhotosCount, formatDate, formatDuration, VHandIcon };

/**
 * TripCard - Reusable trip card component
 * @param {Object} trip - Trip data
 * @param {boolean} showEditButton - Show edit button (default: false)
 * @param {boolean} showPublicationBadge - Show publication status badge (default: true)
 * @param {boolean} showActionButtons - Show hover action buttons (default: false)
 * @param {boolean} showDataOkBadge - Show data_ok badge (default: false, only for owner view)
 * @param {boolean} isPinned - Whether the trip is pinned by current user
 * @param {function} onPinChange - Callback when pin status changes
 * @param {boolean} isAuthenticated - Whether user is authenticated
 * @param {function} onAuthRequired - Callback when auth is required
 * @param {boolean} isOwner - Whether current user owns this trip
 * @param {boolean} isLiked - Whether the trip is liked by current user
 * @param {function} onLikeChange - Callback when like status changes
 */
export const TripCard = ({ 
  trip, 
  showEditButton = false, 
  showPublicationBadge = true,
  showActionButtons = false,
  showDataOkBadge = false,
  isPinned = false,
  onPinChange = null,
  isAuthenticated = false,
  onAuthRequired = null,
  isOwner = false,
  isLiked = false,
  onLikeChange = null
}) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("common");
  const { user: authUser } = useAuth();
  const { plan } = useLimits();
  const currentLang = i18n.language?.split("-")[0] || "pt";
  const [pinned, setPinned] = useState(isPinned);
  const [pinLoading, setPinLoading] = useState(false);
  const [liked, setLiked] = useState(isLiked);
  const [likeLoading, setLikeLoading] = useState(false);
  const [likeCount, setLikeCount] = useState(trip.like_count || 0);
  
  // Auto-detect edit permission
  const canEdit = isOwner || trip.user_role === "collaborator";
  const displayActionButtons = showActionButtons || canEdit;

  // "Planned" visual treatment (orange border + animated golden wave)
  // applies ONLY to planned trips that are still upcoming/ongoing. A
  // planned trip whose dates are already in the PAST (planning_status
  // === "past_dates") is effectively a completed trip, so it drops the
  // future/planned look — consistent with the planning-status badge,
  // which also hides for "past_dates". `trip_type` itself is a manual
  // field that doesn't auto-flip, so we derive this from the (runtime)
  // planning_status the backend already attaches to listings.
  const showPlannedStyle = trip.trip_type === "planned" && trip.planning_status !== "past_dates";
  const displayEditButton = showEditButton || canEdit;
  
  // Get translated motorcycle nickname
  const motoNickname = trip.motorcycle ? getTranslatedValue(trip.motorcycle.nickname, currentLang) : null;
  
  // Get publication status info with translated label
  const pubStatus = trip.publication_status || "draft";
  const pubConfig = PUBLICATION_STATUS_CONFIG[pubStatus] || PUBLICATION_STATUS_CONFIG.draft;
  const PubIcon = pubConfig.icon;
  const pubLabel = t(`badges.publication_status.${pubStatus}`, pubStatus);

  const handlePinToggle = useCallback(async (e) => {
    e.stopPropagation();
    
    if (!isAuthenticated) {
      if (onAuthRequired) {
        onAuthRequired();
      } else {
        handleUnauthorizedAccess(navigate);
      }
      return;
    }
    
    setPinLoading(true);
    try {
      if (pinned) {
        await api.delete(`/api/trips/${trip.trip_id}/pin`);
        setPinned(false);
        toast.success(t("trip_card.removed_from_favorites"));
      } else {
        await api.post(`/api/trips/${trip.trip_id}/pin`);
        setPinned(true);
        toast.success(t("trip_card.added_to_favorites"));
      }
      if (onPinChange) {
        onPinChange(trip.trip_id, !pinned);
      }
    } catch (error) {
      toast.error(t("trip_card.error_updating_favorites"));
    } finally {
      setPinLoading(false);
    }
  }, [isAuthenticated, onAuthRequired, navigate, pinned, trip.trip_id, onPinChange, t]);

  const handleLikeToggle = useCallback(async (e) => {
    e.stopPropagation();
    
    if (!isAuthenticated) {
      if (onAuthRequired) {
        onAuthRequired();
      } else {
        handleUnauthorizedAccess(navigate);
      }
      return;
    }
    
    setLikeLoading(true);
    try {
      if (liked) {
        const res = await api.delete(`/api/trips/${trip.trip_id}/like`);
        setLiked(false);
        setLikeCount(res.data.like_count || 0);
        toast.success(t("trip_card.like_removed"));
      } else {
        const res = await api.post(`/api/trips/${trip.trip_id}/like`);
        setLiked(true);
        setLikeCount(res.data.like_count || 0);
        toast.success(t("trip_card.like_added"));
      }
      if (onLikeChange) {
        onLikeChange(trip.trip_id, !liked);
      }
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data?.detail || t("trip_card.cannot_like"));
      } else {
        toast.error(t("trip_card.error_updating_like"));
      }
    } finally {
      setLikeLoading(false);
    }
  }, [isAuthenticated, onAuthRequired, navigate, liked, trip.trip_id, onLikeChange, t]);

  const handleCardClick = useCallback(() => {
    navigate(`/passeio/${trip.trip_id}`);
  }, [navigate, trip.trip_id]);

  const handleEditClick = useCallback((e) => {
    e.stopPropagation();
    navigate(`/passeio/${trip.trip_id}/editar`);
  }, [navigate, trip.trip_id]);

  return (
    <Card 
      className={`trip-card bg-card rounded-xl overflow-hidden group cursor-pointer flex flex-col h-full relative ${
        showPlannedStyle ? "border-2 border-orange-500/80" : "border-border"
      }`}
      data-testid={`trip-card-${trip.trip_id}`}
      onClick={handleCardClick}
    >
      {/* Photo Preview */}
      <div className="relative">
        {tripHasPhotos(trip) ? (
          <div className="h-40 bg-muted overflow-hidden">
            <img 
              src={getMainPhotoThumbnail(trip)}
              alt={`${getTranslatedValue(trip.title, currentLang)} — ${t("trip_card.alt_suffix")}`}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
            />
            <div className="h-40 bg-muted items-center justify-center border-b border-border" style={{ display: 'none' }}>
              <div className="text-center">
                <ImageIcon className="h-10 w-10 text-muted-foreground mx-auto mb-1" strokeWidth={1} />
                <p className="text-xs text-muted-foreground">{t("trip_card.no_photos")}</p>
              </div>
            </div>
          </div>
        ) : trip.planning_status === "waiting" || trip.planning_status === "enrollment" ? (
          <div className="h-40 bg-muted overflow-hidden">
            <img 
              src="/images/scheduled-placeholder.jpg"
              alt={t("trip_card.scheduled_placeholder", "Passeio planeado")}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        ) : trip.planning_status === "ongoing" ? (
          <div className="h-40 bg-muted overflow-hidden">
            <img 
              src="/images/ongoing-placeholder.jpg"
              alt={t("trip_card.ongoing_placeholder", "Passeio a decorrer")}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        ) : (
          <div className="h-40 bg-muted flex items-center justify-center border-b border-border">
            <div className="text-center">
              <ImageIcon className="h-10 w-10 text-muted-foreground mx-auto mb-1" strokeWidth={1} />
              <p className="text-xs text-muted-foreground">{t("trip_card.no_photos")}</p>
            </div>
          </div>
        )}
        
        {/* Badges Row - bottom of photo */}
        <div className="absolute bottom-2 left-2 flex items-center gap-1.5 flex-wrap max-w-[calc(100%-16px)]">
          {/* Publication Status Badge — only editors (owner/collaborator) see it;
              it's an editing concept, redundant for public/non-editor viewers */}
          {showPublicationBadge && canEdit && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-black/70 backdrop-blur-sm text-[10px] font-medium ${pubConfig.color}`}>
                    <PubIcon className="h-3 w-3" />
                    <span>{pubLabel}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t(`badges.publication_status.${pubStatus}_tooltip`, pubStatus)}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          
          {/* Data OK Badge */}
          {showDataOkBadge && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span 
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium border backdrop-blur-sm ${trip.data_ok !== false 
                      ? "bg-green-900/80 text-green-300 border-green-600/50" 
                      : "bg-red-900/80 text-red-300 border-red-600/50"
                    }`}
                  >
                    {trip.data_ok !== false ? (
                      <ThumbsUp className="h-3 w-3" />
                    ) : (
                      <ThumbsDown className="h-3 w-3" />
                    )}
                    {t("trip_card.data_badge")}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{trip.data_ok !== false ? t("trip_card.data_ok_tooltip") : t("trip_card.data_incomplete_tooltip")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          
          {/* Motri Badge */}
          {trip.created_via === "motri" && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isAuthenticated) return navigate("/motri-apresentacao");
                      const hasMotriAccess = authUser?.role === "owner" || authUser?.role === "admin" || plan?.plan_slug === "road_master";
                      navigate(hasMotriAccess ? "/motri" : "/motri-apresentacao");
                    }}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-black/70 text-orange-300 border border-orange-500/50 backdrop-blur-sm hover:bg-black/80 transition-colors cursor-pointer"
                    data-testid={`motri-badge-${trip.trip_id}`}
                  >
                    <img src={MOTRI_AVATAR} alt="Motri" className="w-3.5 h-3.5 rounded-full object-cover" />
                    Motri
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">{t("trip_card.motri_badge_tooltip", "Criado com o assistente Motri")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          
          {/* Planning Status Badge — only for planned active trips */}
          {trip.planning_status && trip.planning_status !== "past_dates" && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium backdrop-blur-sm cursor-default ${
                    trip.planning_status === "ongoing" 
                      ? "bg-blue-900/80 text-blue-200 border border-blue-500/50"
                      : "bg-black/70 text-orange-300 border border-orange-500/50"
                  }`} data-testid={`planning-badge-${trip.trip_id}`}>
                    <PlayCircle className="h-3 w-3" />
                    {t(`badges.planning_status.${trip.planning_status}`, trip.planning_status)}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t(`badges.planning_status.${trip.planning_status}_tooltip`, "")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          
          {/* User Role Badge - Owner, Participant or Collaborator */}
          {trip.user_role === "owner" && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-black/70 text-orange-300 border border-orange-500/50 backdrop-blur-sm cursor-default" data-testid={`role-badge-${trip.trip_id}`}>
                    <Star className="h-3 w-3" />
                    {t("trip_card.role_badge.owner")}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("trip_card.role_badge.owner_tooltip")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {trip.user_role === "participant" && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-blue-900/80 text-blue-300 border border-blue-600/50 backdrop-blur-sm cursor-default" data-testid={`role-badge-${trip.trip_id}`}>
                    <Users className="h-3 w-3" />
                    {t("trip_card.role_badge.participant")}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("trip_card.role_badge.participant_tooltip")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {trip.user_role === "collaborator" && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-purple-900/80 text-purple-300 border border-purple-600/50 backdrop-blur-sm cursor-default" data-testid={`role-badge-${trip.trip_id}`}>
                    <Edit className="h-3 w-3" />
                    {t("trip_card.role_badge.collaborator")}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("trip_card.role_badge.collaborator_tooltip")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        
        {/* Action Buttons - shown on hover */}
        {displayActionButtons && (
          <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="h-8 w-8 p-0 bg-black/70 backdrop-blur-sm hover:bg-primary border border-white/30 text-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/passeio/${trip.trip_id}`);
                    }}
                    data-testid={`view-trip-${trip.trip_id}`}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("trip_card.view_trip")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {displayEditButton && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="h-8 w-8 p-0 bg-black/70 backdrop-blur-sm hover:bg-primary border border-white/30 text-white"
                      onClick={handleEditClick}
                      data-testid={`edit-trip-${trip.trip_id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t("trip_card.edit_trip")}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )}
      </div>
      
      <CardContent className="p-4 flex-1 flex flex-col relative overflow-hidden">
        {/* Orange wave overlay on dark background for planned trips
            (hidden once the planned dates are in the past). */}
        {showPlannedStyle && (
          <div className="absolute top-0 left-0 right-0 pointer-events-none">
            <svg viewBox="0 0 400 80" preserveAspectRatio="none" className="w-full h-24 block">
              <path d="M0,0 L400,0 L400,35 C360,50 310,30 250,48 C190,58 140,35 80,50 C40,58 15,42 0,55 Z" fill="rgba(255,190,0,0.2)">
                <animate attributeName="d" dur="6s" repeatCount="indefinite" values="
                  M0,0 L400,0 L400,35 C360,50 310,30 250,48 C190,58 140,35 80,50 C40,58 15,42 0,55 Z;
                  M0,0 L400,0 L400,45 C350,32 300,52 240,38 C180,30 130,52 70,40 C30,34 10,50 0,48 Z;
                  M0,0 L400,0 L400,35 C360,50 310,30 250,48 C190,58 140,35 80,50 C40,58 15,42 0,55 Z
                " />
              </path>
              <path d="M0,0 L400,0 L400,40 C355,52 300,35 240,50 C180,60 130,40 70,54 C30,60 10,46 0,58 Z" fill="rgba(255,190,0,0.14)">
                <animate attributeName="d" dur="8s" repeatCount="indefinite" values="
                  M0,0 L400,0 L400,40 C355,52 300,35 240,50 C180,60 130,40 70,54 C30,60 10,46 0,58 Z;
                  M0,0 L400,0 L400,50 C345,38 290,55 230,42 C170,36 120,54 60,44 C25,38 8,52 0,50 Z;
                  M0,0 L400,0 L400,40 C355,52 300,35 240,50 C180,60 130,40 70,54 C30,60 10,46 0,58 Z
                " />
              </path>
              <path d="M0,0 L400,0 L400,44 C350,56 285,38 220,52 C155,62 100,42 50,56 C20,62 5,50 0,60 Z" fill="rgba(255,190,0,0.08)">
                <animate attributeName="d" dur="10s" repeatCount="indefinite" values="
                  M0,0 L400,0 L400,44 C350,56 285,38 220,52 C155,62 100,42 50,56 C20,62 5,50 0,60 Z;
                  M0,0 L400,0 L400,54 C340,42 275,58 210,44 C145,36 90,56 40,46 C15,40 3,54 0,52 Z;
                  M0,0 L400,0 L400,44 C350,56 285,38 220,52 C155,62 100,42 50,56 C20,62 5,50 0,60 Z
                " />
              </path>
            </svg>
          </div>
        )}
        <h3 className="font-bold text-lg mb-1 text-foreground">{getTranslatedValue(trip.title, currentLang)}</h3>
        
        {/* Countries */}
        {trip.countries?.length > 0 && (
          <p className="text-sm text-muted-foreground mb-2 font-bold" data-testid={`trip-countries-${trip.trip_id}`}>
            {trip.countries.map(c => getTranslatedValue(c, currentLang) || c).join(", ")}
          </p>
        )}
        
        {/* Author — "Clube: " for club trips, "Criador: " for personal */}
        {trip.club?.name ? (
          <div className="flex items-center gap-2 mb-3" data-testid={`trip-club-${trip.trip_id}`}>
            <span className="text-xs text-muted-foreground whitespace-nowrap">{t("trip_card.club_label", "Clube")}:</span>
            <Avatar className="h-8 w-8 border-2 border-card">
              <AvatarImage src={getAvatarThumbnail(trip.club.logo || trip.club.photo, 64)} alt={getTranslatedValue(trip.club.name, currentLang)} />
              <AvatarFallback className="text-xs bg-primary/10 text-primary">
                {(getTranslatedValue(trip.club.name, currentLang) || "C").charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <span className="text-xs text-muted-foreground truncate max-w-[150px]">{getTranslatedValue(trip.club.name, currentLang)}</span>
          </div>
        ) : trip.owner ? (
          <div className="flex items-center gap-2 mb-3" data-testid={`trip-owner-${trip.trip_id}`}>
            <span className="text-xs text-muted-foreground whitespace-nowrap">{t("trip_card.motard_label", "Motard")}:</span>
            <Avatar className="h-8 w-8 border-2 border-card">
              <AvatarImage src={getAvatarThumbnail(trip.owner.picture, 64)} alt={trip.owner.name} />
              <AvatarFallback className="text-xs bg-primary/10 text-primary">
                {trip.owner.name?.charAt(0)?.toUpperCase() || "?"}
              </AvatarFallback>
            </Avatar>
            <span className="text-xs text-muted-foreground truncate max-w-[150px]">{trip.owner.nickname || trip.owner.name}</span>
            {trip.owner.badge && <PlanBadgeIcon badge={trip.owner.badge} size="h-3 w-3" />}
          </div>
        ) : null}
        
        {/* Separator after author */}
        <div className="border-t border-white/20 mb-3" />
        
        {/* Data grid — 2 columns: label + value */}
        <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs mb-3">
          {/* Date */}
          {trip.start_date && (
            <>
              <span className="text-muted-foreground whitespace-nowrap" data-testid={`trip-dates-label-${trip.trip_id}`}>
                {trip.end_date && trip.end_date !== trip.start_date 
                  ? `${t("trip_card.dates_label", "Datas")}:` 
                  : `${t("trip_card.date_label", "Data")}:`}
              </span>
              <span className="font-mono text-muted-foreground" data-testid={`trip-dates-${trip.trip_id}`}>
                {formatDate(trip.start_date)}
                {trip.end_date && trip.end_date !== trip.start_date && ` - ${formatDate(trip.end_date)}`}
              </span>
            </>
          )}

          {/* Distance */}
          {trip.distance_km > 0 && (
            <>
              <span className="text-muted-foreground whitespace-nowrap">{t("trip_card.distance_label", "Distância")}:</span>
              <span className="font-mono text-primary">{trip.distance_km} km</span>
            </>
          )}

          {/* Duration */}
          {trip.duration_hours > 0 && (
            <>
              <span className="text-muted-foreground whitespace-nowrap">{t("trip_card.duration_label", "Duração")}:</span>
              <span className="font-mono text-primary">{formatDuration(trip.duration_hours)}</span>
            </>
          )}

          {/* Type */}
          {getTripTypes(trip).length > 0 && (
            <>
              <span className="text-muted-foreground whitespace-nowrap">{t("trip_card.type_label", "Tipo")}:</span>
              <span className="flex items-center gap-1">
                {(() => {
                  const types = getTripTypes(trip);
                  if (types.length === 1) {
                    const type = types[0];
                    const TypeIcon = TRIP_TYPE_CONFIG[type]?.icon || Route;
                    const typeColor = TRIP_TYPE_CONFIG[type]?.color || "text-muted-foreground";
                    return (
                      <>
                        <TypeIcon className={`h-3 w-3 ${typeColor}`} />
                        <span className={typeColor}>{t(`search.types.${type}`, type)}</span>
                      </>
                    );
                  }
                  return (
                    <>
                      <Route className="h-3 w-3 text-muted-foreground" />
                      <span className="text-muted-foreground">{t('trip_detail.type_mixed', 'Misto')}</span>
                    </>
                  );
                })()}
              </span>
            </>
          )}

          {/* Difficulty */}
          {getTripDifficulties(trip).length > 0 && (
            <>
              <span className="text-muted-foreground whitespace-nowrap">{t("trip_card.difficulty_label", "Dificuldade")}:</span>
              <span className="flex items-center gap-1.5">
                {(() => {
                  const diffs = getTripDifficulties(trip);
                  const maxDiff = diffs.reduce((a, b) => {
                    const order = { green: 0, blue: 1, red: 2, black: 3 };
                    return (order[b] || 0) > (order[a] || 0) ? b : a;
                  }, diffs[0]);
                  const diffColor = {
                    green: "text-green-500",
                    blue: "text-blue-500",
                    red: "text-yellow-500",
                    black: "text-red-500"
                  }[maxDiff] || "text-muted-foreground";
                  const barColor = {
                    green: "bg-green-500",
                    blue: "bg-blue-500",
                    red: "bg-yellow-500",
                    black: "bg-red-500"
                  }[maxDiff] || "bg-current";
                  const maxDiffOrder = { green: 0, blue: 1, red: 2, black: 3 }[maxDiff] || 0;
                  return (
                    <>
                      <span className="flex gap-0.5">
                        {[0, 1, 2, 3].map((i) => (
                          <div
                            key={i}
                            className={`w-1 h-2.5 rounded-sm ${i <= maxDiffOrder ? barColor : "bg-muted-foreground/30"}`}
                          />
                        ))}
                      </span>
                      <span className={diffColor}>
                        {diffs.length === 1
                          ? t(`search.difficulties.${diffs[0]}`, diffs[0])
                          : t('trip_detail.difficulty_mixed', 'Mista')}
                      </span>
                    </>
                  );
                })()}
              </span>
            </>
          )}
        </div>

        {/* Separator before participants */}
        {trip.participants?.length > 0 && <div className="border-t border-white/20 mb-3" />}

        {/* Participants — larger avatars, wrap to second line */}
        {trip.participants?.length > 0 && (
          <div className="mb-3" data-testid={`trip-participants-${trip.trip_id}`}>
            <div className="flex items-center gap-1.5 mb-1.5">
              <Users className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="text-xs text-muted-foreground">{t("trip_card.participants_label", "Participantes")}:</span>
            </div>
            <TooltipProvider>
              <div className="flex flex-wrap gap-1">
                {[...trip.participants].sort((a, b) => {
                  const roleOrder = { owner: 0, moderator: 1, member: 2 };
                  const ra = roleOrder[a.club_role] ?? 3;
                  const rb = roleOrder[b.club_role] ?? 3;
                  if (ra !== rb) return ra - rb;
                  return (a.name || "").localeCompare(b.name || "", "pt");
                }).map((participant) => (
                  <Tooltip key={participant.participant_id}>
                    <TooltipTrigger>
                      <Avatar className="h-8 w-8 border-2 border-card">
                        <AvatarImage 
                          src={getAvatarThumbnail(participant.user_picture || participant.photo, 64)} 
                          alt={participant.name}
                        />
                        <AvatarFallback className="text-xs bg-primary/10 text-primary">
                          {participant.name?.charAt(0)?.toUpperCase() || "?"}
                        </AvatarFallback>
                      </Avatar>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{participant.name}</p>
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
            </TooltipProvider>
          </div>
        )}

        {/* Bottom Stats - Segments, Places, Photos - Always at bottom */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-auto pt-3 border-t border-white/20">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1 cursor-default">
                  <Route className="h-3 w-3" />
                  <span>{trip.segments?.length || trip.route_links?.length || 0} {(trip.segments?.length || trip.route_links?.length || 0) === 1 ? t("trip_card.segment") : t("trip_card.segments")}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("trip_card.tooltip_segments")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1 cursor-default">
                  <MapPin className="h-3 w-3" />
                  <span>{(trip.places?.length || 0) + (trip.localities?.length || 0)} {((trip.places?.length || 0) + (trip.localities?.length || 0)) === 1 ? t("trip_card.place") : t("trip_card.places")}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("trip_card.tooltip_places")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1 cursor-default">
                  <ImageIcon className="h-3 w-3" />
                  <span>{getTotalPhotosCount(trip)} {getTotalPhotosCount(trip) === 1 ? t("trip_card.photo") : t("trip_card.photos")}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("trip_card.tooltip_photos")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardContent>
      
      {/* Action Bar - Pin & Like toggles - ALWAYS at bottom */}
      <div className="mt-auto px-4 py-2 bg-muted/40 border-t border-border/50 flex items-center justify-center gap-8">
        {/* Pin Toggle - available for ALL trips */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handlePinToggle}
                disabled={pinLoading}
                className={`p-2 rounded-full transition-all ${
                  pinned 
                    ? 'text-primary bg-primary/20 hover:bg-primary/30' 
                    : 'text-white/70 hover:text-primary hover:bg-primary/10'
                } ${pinLoading ? 'opacity-50 cursor-wait' : ''}`}
                data-testid={`pin-trip-${trip.trip_id}`}
              >
                <Pin className={`h-5 w-5 ${pinned ? 'fill-primary' : ''}`} />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              {pinned ? t("trip_card.remove_from_favorites") : t("trip_card.add_to_favorites")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        {/* Like Toggle - only for OTHER people's trips and NOT collaborators */}
        {!isOwner && trip.user_role !== "collaborator" && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleLikeToggle}
                  disabled={likeLoading}
                  className={`p-2 rounded-full transition-all flex items-center gap-1 ${
                    liked 
                      ? 'text-red-500 hover:bg-red-500/10' 
                      : 'text-white/70 hover:text-red-500 hover:bg-muted/50'
                  } ${likeLoading ? 'opacity-50 cursor-wait' : ''}`}
                  data-testid={`like-trip-${trip.trip_id}`}
                >
                  <ThumbsUp className={`h-5 w-5 ${liked ? 'fill-red-500' : ''}`} />
                  {likeCount > 0 && (
                    <span className="text-sm font-medium">{likeCount}</span>
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                {liked ? t("trip_card.remove_like") : t("trip_card.add_like")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        
        {/* Like counter for owner's own trips OR for collaborators */}
        {(isOwner || trip.user_role === "collaborator") && likeCount > 0 && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="p-2 rounded-full text-red-500/70 flex items-center gap-1">
                  <ThumbsUp className="h-4 w-4" />
                  <span className="text-sm font-medium">{likeCount}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {likeCount} {likeCount === 1 ? 'like' : 'likes'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </Card>
  );
};

// Memoize TripCard to prevent unnecessary re-renders in lists
export default memo(TripCard);
